import {
  Promotion,
  PromotionDisplayType,
  PromotionType,
  ShopType,
} from "src/types/graphql.d";
import {
  Margins,
  PromoTimerContainer,
  PromoTimerMessage,
} from "./PromoTimer.styled";
import { Timer } from "src/atoms/Timer";
import { usePromotionContext } from "src/hooks/usePromotionContext";
import { Trans } from "@lingui/macro";
import { BellRingingTilted } from "src/icons/BellRingingTilted";
import { useEffect, useRef } from "react";
import { usePromotion } from "src/hooks/usePromotion";

type PromoTimerProps = {
  promotion?: Promotion;
  shopType?: ShopType;
  slug?: string;
  promotionType?: PromotionType;
  margins?: Margins;
};

function PromoTimer({
  promotion,
  slug,
  promotionType,
  shopType,
  margins,
}: PromoTimerProps): JSX.Element | null {
  const isPromotionContextSet = useRef<boolean>(false);
  const { data, loading } = usePromotion(
    slug || "",
    promotionType || PromotionType.Brand,
    shopType || ShopType.Women,
    !!promotion
  );
  const { promotionContextDispatch, promotionContextState } =
    usePromotionContext();

  const promotionData = promotion || data?.promotion;

  useEffect(() => {
    if (
      !loading &&
      !isPromotionContextSet.current &&
      promotionData &&
      "promo_display_type" in promotionData
    ) {
      promotionContextDispatch({
        type: "SET_PROMO",
        payload: {
          promotionDisplayType:
            promotionData?.promo_display_type || PromotionDisplayType.Default,
          promotionEndDate: promotionData?.promo_end_date || "",
          promotionMessage: promotionData?.promo_message || "",
          promotionStartDate: promotionData?.promo_start_date || "",
          promotion: promotionData,
        },
      });

      isPromotionContextSet.current = true;
    }
  }, [loading, promotionData, promotionContextDispatch]);

  if (
    !promotionData ||
    promotionData?.promo_display_type !== PromotionDisplayType.FlashSale
  ) {
    return null;
  }

  const onTimerEnd = (hasPromotionEnded: boolean) =>
    promotionContextDispatch({
      type: "SET_PROMO_STATUS",
      payload: hasPromotionEnded,
    });

  const isFlashSale =
    promotionData.promo_display_type === PromotionDisplayType.FlashSale;

  return (
    <PromoTimerContainer
      data-testid="promo-timer"
      margins={margins}
      textColor={
        isFlashSale ? undefined : promotionData.promo_text_color || undefined
      }
      backgroundColor={
        isFlashSale
          ? undefined
          : promotionData.promo_background_color || undefined
      }
    >
      <PromoTimerMessage
        textColor={
          isFlashSale ? undefined : promotionData.promo_text_color || undefined
        }
      >
        {!promotionContextState.hasPromotionEnded ? (
          <Trans>Flash Sale!</Trans>
        ) : (
          <>
            <BellRingingTilted />
            <Trans>This sale has ended. New flash sales coming soon.</Trans>
          </>
        )}
      </PromoTimerMessage>
      {!promotionContextState.hasPromotionEnded ? (
        <Timer
          endDate={promotionData.promo_end_date || ""}
          theme="promotimer"
          onTimerEnd={onTimerEnd}
        />
      ) : (
        false
      )}
    </PromoTimerContainer>
  );
}

export { PromoTimer };
